// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { productListCategoryFiltersStyles } from "@insite/content-library/Widgets/ProductList/ProductListCategoryFilters";
import ProductListFiltersAccordionSection, {
    ProductListFilterAccordionSectionStyles,
} from "@insite/content-library/Widgets/ProductList/ProductListFilterAccordionSection";
import Link from "@insite/mobius/Link/Link";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    expandByDefault = "expandByDefault",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.expandByDefault]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
    // XNG-Change
    customSettings: getSettingsCollection(state).customSettings,
});

const mapDispatchToProps = {
    addProductFilters,
    removeProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export const productListAttributeFiltersStyles: ProductListFilterAccordionSectionStyles = {};

const styles = productListAttributeFiltersStyles;

const ProductListAttributeFilters: FC<Props> = ({
    attributeTypeFacets,
    addProductFilters,
    removeProductFilters,
    fields,
    customSettings,
}) => {
    const [expanded, setExpanded] = useState(false);

    // XNG-Change: use a setting
    const showMoreLimit = (customSettings as any).maximumAttributeTypes || 10;
    // XNG-Change
    const limitedFacets = expanded ? attributeTypeFacets : attributeTypeFacets?.slice(0, showMoreLimit);

    const onChangeFacet = (facet: FacetModel) => {
        if (facet.selected) {
            removeProductFilters({ attributeValueIds: [facet.id] });
        } else {
            addProductFilters({ attributeValueIds: [facet.id] });
        }
    };

    // XNG-Change
    if (!attributeTypeFacets?.length) {
        return null;
    }

    return (
        <>
            {limitedFacets?.map(a =>
                a.attributeValueFacets ? (
                    <ProductListFiltersAccordionSection
                        key={a.attributeTypeId}
                        title={translate(a.nameDisplay)}
                        facets={a.attributeValueFacets.map<FacetModel>(av => ({
                            ...av,
                            id: av.attributeValueId,
                            name: av.valueDisplay,
                        }))}
                        onChangeFacet={onChangeFacet}
                        showMoreLimit={showMoreLimit}
                        expandByDefault={fields.expandByDefault}
                        extendedStyles={productListCategoryFiltersStyles}
                    />
                ) : null,
            )}

            {/* XNG-Change: add show more/less links */}
            {limitedFacets &&
                attributeTypeFacets.length > showMoreLimit &&
                (expanded ? (
                    <Link {...styles.seeLessLink} onClick={() => setExpanded(false)}>
                        {translate("Show Less")}
                    </Link>
                ) : (
                    <Link {...styles.seeAllLink} onClick={() => setExpanded(true)}>
                        {translate("Show More")}
                    </Link>
                ))}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListAttributeFilters),
    definition: {
        group: "Product List",
        displayName: "Attribute Filters",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.expandByDefault,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
